import { trackAutoship } from '@libs/analytics';
import type { AvailableStatus, EventData, Order, OrderItem, SubscriptionItem, WindowWithOgApi } from './types';
import { getCurrency } from '@libs/core/utils';

export const trackEvent = async (type: string) => {
    async function getLatestSubscriptions(): Promise<SubscriptionItem[]> {
        const results = await (window as unknown as WindowWithOgApi).og.smi.api.request_subscriptions();
        return results.results.sort((a: SubscriptionItem, b: SubscriptionItem) => {
            return new Date(b.updated).getTime() - new Date(a.updated).getTime();
        });
    }

    async function getLatestOrders(status: string[]): Promise<Order[]> {
        const results = await (window as unknown as WindowWithOgApi).og.smi.api.request_orders({
            status: status,
        });
        return results.results.sort((a: Order, b: Order) => {
            return new Date(b.updated).getTime() - new Date(a.updated).getTime();
        });
    }

    async function getLatestOrderItems(status: string[]): Promise<OrderItem[]> {
        const orderItems = await getOrderItems(status);
        orderItems.sort((a: OrderItem, b: OrderItem) => {
            return new Date(b.order_updated).getTime() - new Date(a.order_updated).getTime();
        });
        // 2s gap to get modified order itens
        const endUpdatedRange = new Date(orderItems[0].order_updated).getTime();
        const startUpdatedRange = new Date(orderItems[0].order_updated).getTime() - 2000;
        return orderItems.filter(oi => {
            const oiUpdated = new Date(oi.order_updated).getTime();
            return oiUpdated >= startUpdatedRange && oiUpdated <= endUpdatedRange;
        });
    }

    async function getOrderItems(status: string[]): Promise<OrderItem[]> {
        const results = await (window as unknown as WindowWithOgApi).og.smi.api.request_orders_items({
            status: status,
        });
        return results.results;
    }

    async function getEventForLatestSubscription(status: AvailableStatus = ['UNSENT']): Promise<EventData> {
        const results = await Promise.all([getLatestSubscriptions(), getOrderItems(status)]);
        const subscriptions = results[0];
        const orderItems = results[1];

        const eventData = {
            subscription: subscriptions[0],
            orderItem: orderItems.find(oi => oi.subscription === subscriptions[0].public_id),
        };
        const eventObject = {
            public_id: eventData.subscription.public_id,
            currency_code: getCurrency(),
            frequency_days: eventData.subscription.frequency_days,
            product: eventData.subscription.product,
            quantity: eventData.orderItem?.quantity ?? null,
            price: eventData.orderItem?.price ?? null,
            total_price: eventData.orderItem?.total_price ?? null,
            place: null,
            cancel_reason: eventData.subscription.cancel_reason,
            reminder_days: eventData.subscription.reminder_days,
        };
        return eventObject;
    }

    async function getEventsForLatestOrder(status: AvailableStatus = ['UNSENT']): Promise<EventData[]> {
        const results = await Promise.all([getLatestOrders(status), getLatestSubscriptions(), getOrderItems(status)]);

        const order = results[0][0];
        const subscriptions = results[1];
        const orderItems = results[2];

        const orderItemsFiltered = orderItems.filter(oi => oi.order == order?.public_id);

        const eventDataList: EventData[] = [];
        orderItemsFiltered.forEach(orderItem => {
            const subscription = subscriptions.find(subs => subs.public_id == orderItem.subscription);
            eventDataList.push({
                public_id: subscription?.public_id ?? null,
                currency_code: getCurrency(),
                frequency_days: subscription?.frequency_days ?? null,
                product: subscription?.product ?? null,
                quantity: orderItem.quantity,
                price: orderItem.price,
                total_price: orderItem.total_price,
                place: order?.place ?? null,
                cancel_reason: subscription?.cancel_reason ?? null,
                reminder_days: subscription?.reminder_days ?? null,
            });
        });
        return eventDataList;
    }

    async function getEventsForLatestOrderItems(status: AvailableStatus = ['UNSENT']): Promise<EventData[]> {
        const results = await Promise.all([getLatestOrderItems(status), getLatestSubscriptions()]);

        const orderItems = results[0];
        const subscriptions = results[1];

        const eventDataList: EventData[] = [];
        orderItems.forEach(orderItem => {
            const subscription = subscriptions.find(subs => subs.public_id == orderItem.subscription);
            eventDataList.push({
                public_id: subscription?.public_id ?? null,
                currency_code: getCurrency(),
                frequency_days: subscription?.frequency_days ?? null,
                product: subscription?.product ?? null,
                quantity: orderItem.quantity,
                price: orderItem.price,
                total_price: orderItem.total_price,
                place: null,
                cancel_reason: subscription?.cancel_reason ?? null,
                reminder_days: subscription?.reminder_days ?? null,
            });
        });
        return eventDataList;
    }

    const mapper: Record<string, () => Promise<void>> = {
        reactivate: async () => {
            const sub = await getEventForLatestSubscription();
            trackAutoship(
                'reactivate_subscription',
                sub.total_price,
                sub.public_id,
                sub.currency_code,
                sub.frequency_days,
                sub.product,
                Number(sub.price),
                sub.quantity,
                { subscription_type: 'reactivate' }
            );
        },
        sendNow: async () => {
            const events = await getEventsForLatestOrder(['SEND_NOW']);
            events.forEach(event => {
                trackAutoship(
                    'send_subscription_now',
                    event.total_price,
                    event.public_id,
                    event.currency_code,
                    event.frequency_days,
                    event.product,
                    Number(event.price),
                    event.quantity,
                    { subscription_days_remaining: null }
                );
            });
        },
        changeDate: async () => {
            const events = await getEventsForLatestOrder();
            events.forEach(event => {
                trackAutoship(
                    'change_subscription_date',
                    event.total_price,
                    event.public_id,
                    event.currency_code,
                    event.frequency_days,
                    event.product,
                    Number(event.price),
                    event.quantity,
                    { subscription_day_of_month: event.place ? new Date(event.place).getDate() : null }
                );
            });
        },
        skip: async () => {
            const events = await getEventsForLatestOrderItems();
            events.forEach(event => {
                trackAutoship(
                    'skip_subscription',
                    event.total_price,
                    event.public_id,
                    event.currency_code,
                    event.frequency_days,
                    event.product,
                    Number(event.price),
                    event.quantity
                );
            });
        },
        changeFrequency: async () => {
            const sub = await getEventForLatestSubscription();
            trackAutoship(
                'change_subscription_frequency',
                sub.total_price,
                sub.public_id,
                sub.currency_code,
                sub.frequency_days,
                sub.product,
                Number(sub.price),
                sub.quantity
            );
        },
        changeQuantity: async () => {
            const sub = await getEventForLatestSubscription();
            trackAutoship(
                'change_subscription_quantity',
                sub.total_price,
                sub.public_id,
                sub.currency_code,
                sub.frequency_days,
                sub.product,
                Number(sub.price),
                sub.quantity,
                {
                    new_selection: sub.quantity,
                }
            );
        },
        cancel: async () => {
            const sub = await getEventForLatestSubscription(['CANCELLED']);
            trackAutoship(
                'cancel_subscription',
                sub.total_price,
                sub.public_id,
                sub.currency_code,
                sub.frequency_days,
                sub.product,
                Number(sub.price),
                sub.quantity,
                {
                    subscription_type: 'cancel',
                    selection: sub.cancel_reason ?? 'cancel',
                }
            );
        },
        removeItem: async () => {
            // returning updated order products, need to get only rm product
            trackAutoship('remove_from_subscription', null, null, null, null, null, null, null);
        },
        changeProduct: async () => {
            const sub = await getEventForLatestSubscription();
            // Old Product
            trackAutoship('remove_from_subscription', null, null, null, null, null, null, null, {
                subscription_type: 'change product',
            });
            // New Product
            trackAutoship(
                'add_to_subscription',
                sub.total_price,
                sub.public_id,
                sub.currency_code,
                sub.frequency_days,
                sub.product,
                Number(sub.price),
                sub.quantity,
                {
                    subscription_type: 'change product',
                }
            );
        },
    };

    if (mapper[type]) {
        await mapper[type]();
    }
};
